import React, { useState, useEffect, useRef } from 'react';

interface Occupation {
  id: number;
  name: string;
  job_listing_total_count: number;
  kind?: string | null;
  children?: Occupation[];
}

type KindType = 'engineer' | 'marketer' | 'designer' | 'director' | 'consultant';

interface KindCategory {
  kind: KindType;
  label: string;
  order: number;
}

interface OccupationsFilterDropdownProps {
  show: boolean;
  onHide: () => void;
  onApply: (occupationIds: number[]) => void;
  selectedOccupationIds: number[];
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const OccupationsFilterDropdown: React.FC<OccupationsFilterDropdownProps> = ({
  show,
  onHide,
  onApply,
  selectedOccupationIds,
  buttonRef
}) => {
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>(selectedOccupationIds);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedIds(selectedOccupationIds);
  }, [selectedOccupationIds]);

  useEffect(() => {
    if (show) {
      fetchOccupations();
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onHide, buttonRef]);

  const fetchOccupations = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/occupations');
      const data = await response.json();
      setOccupations(data);
    } catch (error) {
      console.error('Failed to fetch occupations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOccupationToggle = (occupationId: number) => {
    const newSelection = selectedIds.includes(occupationId) 
      ? selectedIds.filter(id => id !== occupationId)
      : [...selectedIds, occupationId];
    
    setSelectedIds(newSelection);
    
    // 職種選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(selectedIds);
    onHide();
  };

  const handleClear = () => {
    setSelectedIds([]);
    onApply([]);
  };

  // kindカテゴリの定義（番号順に並べる）
  const kindCategories: KindCategory[] = [
    { kind: 'engineer', label: 'エンジニア', order: 1 },
    { kind: 'marketer', label: 'マーケター', order: 2 },
    { kind: 'designer', label: 'デザイナー', order: 3 },
    { kind: 'director', label: 'ディレクター', order: 4 },
    { kind: 'consultant', label: 'コンサルタント', order: 5 }
  ];

  // すべての職種（親と子を含む）を平坦化
  const getAllOccupations = (): Occupation[] => {
    const all: Occupation[] = [];
    occupations.forEach(parent => {
      all.push(parent);
      if (parent.children) {
        all.push(...parent.children);
      }
    });
    return all;
  };

  // kindに属する職種を取得
  const getOccupationsByKind = (kind: KindType): Occupation[] => {
    const allOccupations = getAllOccupations();
    return allOccupations.filter(occ => occ.kind === kind);
  };

  // kindに属する職種数を取得
  const getKindCount = (kind: KindType): number => {
    return getOccupationsByKind(kind).length;
  };

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">職種で絞り込み</h6>
          <button type="button" className="btn-close" onClick={onHide}></button>
        </div>
        
        {loading ? (
          <div className="text-center">読み込み中...</div>
        ) : (
          <div className="accordion">
            {kindCategories.map(category => {
              const kindOccupations = getOccupationsByKind(category.kind);
              const count = kindOccupations.length;
              
              // kindに属する職種がない場合は表示しない
              if (count === 0) return null;

              return (
                <div key={category.kind} className="accordion-item">
                  <h2 className="accordion-header">
                    <button 
                      className="accordion-button collapsed" 
                      type="button" 
                      data-bs-toggle="collapse" 
                      data-bs-target={`#kind-accordion-${category.kind}`}
                      aria-expanded="false"
                    >
                      {category.label} ({count})
                    </button>
                  </h2>
                  <div 
                    id={`kind-accordion-${category.kind}`} 
                    className="accordion-collapse collapse"
                    data-bs-parent=".accordion"
                  >
                    <div className="accordion-body">
                      <div className="row">
                        {kindOccupations.map(occupation => (
                          <div key={occupation.id} className="col-md-6 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`occupation-${occupation.id}`}
                                checked={selectedIds.includes(occupation.id)}
                                onChange={() => handleOccupationToggle(occupation.id)}
                              />
                              <label className="form-check-label" htmlFor={`occupation-${occupation.id}`}>
                                {occupation.name} ({occupation.job_listing_total_count})
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClear}>
            クリア
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply}>
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default OccupationsFilterDropdown;